/**
 * Today's one list: what is stored, and what is merely proposed.
 *
 * The screen deliberately has no separate "suggestions" panel. A set logged from
 * WhatsApp, a set ticked here, and a set not yet done are the same kind of thing at
 * different stages, and showing them as one list is what makes ticking down a session
 * read as one activity rather than two.
 *
 * Ordering: stored exercises first in their stored order — that is the order the session
 * actually happened in — then suggested exercises not yet started. Rearranging what is
 * already logged to match a suggestion would move rows under a thumb mid-session.
 */

import type { DateString, ExerciseSet, LoggedExercise, MuscleGroup, SuggestedExercise, Workout } from "@altrtrack/core";

export interface TodaySet {
  set: ExerciseSet;
  /** Stored in Firestore right now. Unticking it deletes it. */
  done: boolean;
}

export interface TodayRow {
  catalogId: string;
  name: string;
  muscleGroup: MuscleGroup;
  /** The stored exercise, when any of this row is stored. */
  logged: LoggedExercise | null;
  sets: TodaySet[];
  /** Where the proposed sets came from, or null when none are proposed. */
  from: DateString | null;
}

export function mergeToday(
  session: Workout | null,
  suggestion: readonly SuggestedExercise[],
): TodayRow[] {
  const byId = new Map(suggestion.map((s) => [s.catalogId, s]));
  const rows: TodayRow[] = [];

  for (const logged of session?.exercises ?? []) {
    const proposal = byId.get(logged.catalogId);
    // Only the proposed sets not already stored are still worth offering. Matched by id,
    // not by counting how many are stored: ticking is not required to go top-down — the
    // third row can be ticked before the first — and slicing off "however many are
    // stored" would then keep a set whose id is already written (a duplicate key, doubly
    // rendered, the stored copy showing as un-ticked) while dropping one still proposed.
    // A stored set with no matching id at all (the bot logged it; it was never offered)
    // simply is not in `proposal.sets`, so it neither appears here nor suppresses one
    // that is.
    const storedIds = new Set(logged.sets.map((s) => s.id));
    const remaining = proposal ? proposal.sets.filter((s) => !storedIds.has(s.id)) : [];
    rows.push({
      catalogId: logged.catalogId,
      name: logged.name,
      muscleGroup: logged.muscleGroup,
      logged,
      sets: [
        ...logged.sets.map((set) => ({ set, done: true })),
        ...remaining.map((set) => ({ set, done: false })),
      ],
      from: remaining.length > 0 ? proposal!.from : null,
    });
    byId.delete(logged.catalogId);
  }

  for (const proposal of suggestion) {
    if (!byId.has(proposal.catalogId)) continue;
    rows.push({
      catalogId: proposal.catalogId,
      name: proposal.name,
      muscleGroup: proposal.muscleGroup,
      logged: null,
      sets: proposal.sets.map((set) => ({ set, done: false })),
      from: proposal.from,
    });
  }

  return rows;
}

/*
 * What the day becomes when a set is ticked, unticked or edited.
 *
 * Pure, and separate from the transaction that applies them, for a reason worth stating:
 * `write.ts` is the client Web SDK and the emulator suite drives firebase-admin, so an
 * Admin-SDK test cannot reach it — and re-implementing the append with the Admin SDK to
 * test it would be a second implementation of the thing under test (D-09). Keeping the
 * decisions here means the fast suite holds all of them, and the transaction is left with
 * nothing to get wrong but reading and writing.
 *
 * None of them mutates its argument: the caller is a React render away from state it does
 * not own.
 */

export function withSetAdded(
  workout: Workout,
  exercise: { catalogId: string; name: string; muscleGroup: MuscleGroup },
  set: ExerciseSet,
  newId: () => string,
): Workout {
  const index = workout.exercises.findIndex((e) => e.catalogId === exercise.catalogId);

  // The id is already stored, so this is a duplicate tick — a double-tap, or a retry
  // after a write that actually succeeded. Doing nothing is the correct answer.
  if (index >= 0 && workout.exercises[index]!.sets.some((s) => s.id === set.id)) return workout;

  if (index >= 0) {
    const exercises = [...workout.exercises];
    exercises[index] = { ...exercises[index]!, sets: [...exercises[index]!.sets, set] };
    return { ...workout, exercises };
  }

  return {
    ...workout,
    exercises: [...workout.exercises, { id: newId(), ...exercise, sets: [set] }],
  };
}

export function withSetRemoved(workout: Workout, setId: string): Workout | null {
  const exercises = workout.exercises
    .map((e) => (e.sets.some((s) => s.id === setId) ? { ...e, sets: e.sets.filter((s) => s.id !== setId) } : e))
    // An exercise with no sets left is not a thing that happened.
    .filter((e) => e.sets.length > 0);

  // A day with nothing in it is deleted rather than left as an empty document, so
  // Progress does not count it as a gym day and the ledger does not list it — unless it
  // is marked PT, which is a gym day with or without sets (D-107).
  if (exercises.length > 0 || workout.pt) return { ...workout, exercises };
  return null;
}

export function withSetUpdated(workout: Workout, set: ExerciseSet): Workout {
  return {
    ...workout,
    exercises: workout.exercises.map((e) =>
      e.sets.some((s) => s.id === set.id)
        ? { ...e, sets: e.sets.map((s) => (s.id === set.id ? set : s)) }
        : e,
    ),
  };
}

/**
 * The PT switch (B-56). Off removes the key rather than writing `false`, so "no" has one
 * shape; an unmarked day with nothing in it is deleted, as any empty day is.
 */
export function withPt(workout: Workout, on: boolean): Workout | null {
  if (on) return { ...workout, pt: true };
  const { pt: _pt, ...rest } = workout;
  return rest.exercises.length > 0 ? rest : null;
}
